import { useRef } from 'react';
import type { SetupGuide } from '@/features/setup-guide';
import { ROCKET_STEPS_COMPLETION_MODEL_IDS } from '../rocket-configs';
import { useSetupGuide } from '@/features/setup-guide/hook';

interface UseRocketCompletionProps {
  id?: string | null;
  modelId: keyof SetupGuide | undefined;
  steps: Array<{
    completion: number;
    modelId: keyof SetupGuide;
  }>;
  isReady: boolean;
  onStepCompleted?: ({ rocketId, stepIndex }: { rocketId: string; stepIndex: number }) => void;
  onAllStepsCompleted?: ({ id, areAllRocketsCompleted }: { id: string; areAllRocketsCompleted: boolean }) => void;
}

export function useRocketCompletion({
  id,
  modelId,
  steps,
  isReady,
  onStepCompleted,
  onAllStepsCompleted,
}: UseRocketCompletionProps) {
  const { setupGuideQuery } = useSetupGuide();

  // Store the completed setup guide ids to avoid duplicate calls to onStepCompleted.
  // Just a temporary cache to account for delays in setup guide mutations.
  const completedSetupGuideIds = useRef<Set<string>>(new Set());

  const previousCompletion = useRef<string | null>(null);

  const completion = steps
    .map((step) => step.completion)
    .join(',')
    .concat(id ?? '');

  if (id == null) {
    return;
  }

  const checkCompletion = async () => {
    if (!isReady) {
      return;
    }

    await setupGuideQuery.refetch();

    const setupGuideMutations: (keyof SetupGuide)[] = [];
    for (const [stepIndex, step] of steps.entries()) {
      if (step.completion < 1) {
        continue;
      }
      // Check if the step has been completed in the past

      if (
        completedSetupGuideIds.current.has(step.modelId) ||
        setupGuideQuery.data?.setup_guide?.[step.modelId] != null
      ) {
        continue;
      }

      onStepCompleted?.({ rocketId: id, stepIndex });

      setupGuideMutations.push(step.modelId);
    }

    // If no steps are completed, don't check for rocket completion
    if (setupGuideMutations.length === 0) {
      return;
    }

    const totalCompletedSteps = steps.reduce((accumulator, step) => accumulator + (step.completion >= 1 ? 1 : 0), 0);

    if (totalCompletedSteps === steps.length) {
      // Marks the rocket as completed
      if (modelId != null) {
        setupGuideMutations.push(modelId);
      }

      const areAllRocketsCompleted = ROCKET_STEPS_COMPLETION_MODEL_IDS.every(
        (rocketModelId) => setupGuideQuery.data?.setup_guide?.[rocketModelId] != null || modelId === rocketModelId,
      );

      onAllStepsCompleted?.({ id, areAllRocketsCompleted });
    }

    // Dispatches an event for Journeys to update the setup guide
    // state. This is done to avoid conflicts when updating the
    // setup guide model simultaneously.
    window.dispatchEvent(
      new CustomEvent('hydra:rocket-attribute', {
        detail: {
          type: 'rocket_attribute',
          attribute: setupGuideMutations,
        },
      }),
    );

    for (const modelId of setupGuideMutations) {
      completedSetupGuideIds.current.add(modelId);
    }
  };

  if (previousCompletion.current !== completion) {
    previousCompletion.current = completion;
    checkCompletion();
  }
}
